import LumaAccessiblePanel from '../admin/CoworkFormPanel';
import {Dialog as LumaAccessibleDialog, DialogTitle as LumaAccessibleTitle} from '@headlessui/react';
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import {
    updateReglaNivel,
    ReglaNivelUsuario,
    ReglaNivelUsuarioUpdate
} from '../../services/reglasNivelUsuariosService';
import { fetchUserLevels } from '../../services/userLevelService';

interface EditReglasNivelPageProps {
    regla: ReglaNivelUsuario;
    onClose: () => void;
    onUpdated: (updatedRegla: ReglaNivelUsuario) => void;
}

interface UserLevel {
    id: number;
    nombre: string;
}

const EditReglasNivelPage: React.FC<EditReglasNivelPageProps> = ({ regla, onClose, onUpdated }) => {
    const [formData, setFormData] = useState<ReglaNivelUsuarioUpdate>({
        nombre: regla.nombre,
        nivel_destino: regla.nivel_destino,
        min_citas: regla.min_citas,
        periodo_dias: regla.periodo_dias,
        min_duracion_total_dias: regla.min_duracion_total_dias,
        min_monto_pagado: regla.min_monto_pagado,
        prioridad: regla.prioridad,
        activo: regla.activo,
    });
    const [userLevels, setUserLevels] = useState<UserLevel[]>([]);
    const [loadingLevels, setLoadingLevels] = useState(true);
    const [levelsError, setLevelsError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadUserLevels = async () => {
            try {
                setLoadingLevels(true);
                const data = await fetchUserLevels();
                setUserLevels(data);
            } catch (err) {
                /* Browser diagnostics omitted. */
                setLevelsError('No se pudieron cargar los niveles de usuario');
            } finally {
                setLoadingLevels(false);
            }
        };
        loadUserLevels();
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        let parsedValue: any = value;

        if (type === 'number' || (e.target instanceof HTMLSelectElement && name === 'nivel_destino')) {
            parsedValue = value === '' ? null : Number(value);
        } else if (type === 'checkbox' && e.target instanceof HTMLInputElement) {
            parsedValue = e.target.checked;
        }

        setFormData(prev => ({ ...prev, [name]: parsedValue }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        try {
            const updated = await updateReglaNivel(regla.id, formData);
            onUpdated(updated);
            onClose();
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Error al actualizar la regla de nivel');
            /* Browser diagnostics omitted. */
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <LumaAccessibleDialog open={true} onClose={onClose} data-luma-overlay="true" className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
            <LumaAccessiblePanel data-luma-dialog="true" className="bg-white p-6 rounded-xl w-full max-w-2xl mx-4 border border-gray-100 shadow-xl relative max-h-[90vh] overflow-y-auto custom-scroll">
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <LumaAccessibleTitle className="text-xl font-bold text-gray-900">Editar Regla de Nivel</LumaAccessibleTitle>
                        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6"><div className="luma-form-intro"><span>01</span><div><h3>Información del registro</h3><p>Completa los campos del formulario.</p></div></div>
                    {/* Información General */}
                    <div className="border-b border-gray-200 pb-4">
                        <h4 className="text-md font-semibold text-gray-700 mb-3">Información General</h4>
                        <div className="space-y-4">
                            {/* Fila: Nombre y Nivel Destino */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Nombre <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="nombre"
                                        value={formData.nombre}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Nivel Destino <span className="text-red-500">*</span>
                                    </label>
                                    {loadingLevels ? (
                                        <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500">
                                            Cargando niveles...
                                        </div>
                                    ) : levelsError ? (
                                        <div className="w-full px-3 py-2 border border-red-300 rounded-lg bg-red-50 text-red-700">
                                            {levelsError}
                                        </div>
                                    ) : (
                                        <select
                                            name="nivel_destino"
                                            value={formData.nivel_destino}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="" disabled>Seleccione un nivel</option>
                                            {userLevels.map(level => (
                                                <option key={level.id} value={level.id}>
                                                    {level.nombre}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                            </div>

                            {/* Prioridad (fila completa) */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Prioridad
                                </label>
                                <input
                                    type="number"
                                    name="prioridad"
                                    value={formData.prioridad}
                                    onChange={handleChange}
                                    min="0"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Criterios */}
                    <div className="border-b border-gray-200 pb-4">
                        <h4 className="text-md font-semibold text-gray-700 mb-3">Criterios (opcionales)</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Mínimo de citas
                                </label>
                                <input
                                    type="number"
                                    name="min_citas"
                                    value={formData.min_citas ?? ''}
                                    onChange={handleChange}
                                    min="0"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Período en días
                                </label>
                                <input
                                    type="number"
                                    name="periodo_dias"
                                    value={formData.periodo_dias ?? ''}
                                    onChange={handleChange}
                                    min="1"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Duración total mínima (días)
                                </label>
                                <input
                                    type="number"
                                    name="min_duracion_total_dias"
                                    value={formData.min_duracion_total_dias ?? ''}
                                    onChange={handleChange}
                                    min="1"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Monto pagado mínimo
                                </label>
                                <input
                                    type="text"
                                    name="min_monto_pagado"
                                    value={formData.min_monto_pagado ?? ''}
                                    onChange={handleChange}
                                    placeholder="0.00"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Estado */}
                    <div className="pt-4">
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div>
                                <span className="block text-sm font-medium text-gray-700 mb-1">
                                    Estado
                                </span>
                                <p className="text-xs text-gray-500">
                                    {formData.activo ? 'Regla activa' : 'Regla inactiva'}
                                </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={formData.activo}
                                    onChange={e => setFormData({ ...formData, activo: e.target.checked })}
                                />
                                <div
                                    className={`w-10 h-6 rounded-full transition-colors ${formData.activo ? 'bg-[#3564db]' : 'bg-gray-300'}`}
                                >
                                    <div
                                        className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform ${formData.activo ? 'translate-x-5' : 'translate-x-0'}`}
                                    ></div>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Botones */}
                    <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors font-medium"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={submitting || loadingLevels}
                            className="px-4 py-2 text-sm bg-[#3564db] hover:bg-[#8f1f64] text-white rounded-lg transition-colors font-medium flex items-center justify-center min-w-[120px] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Actualizando...
                                </span>
                            ) : (
                                'Guardar Cambios'
                            )}
                        </button>
                    </div>
                </form>
            </LumaAccessiblePanel>

            {/* Estilos para scroll personalizado */}
            <style>{`
                .custom-scroll::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scroll::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scroll::-webkit-scrollbar-thumb {
                    background-color: rgba(97, 37, 123, 0.3);
                    border-radius: 20px;
                }
                .custom-scroll::-webkit-scrollbar-thumb:hover {
                    background-color: rgba(97, 37, 123, 0.5);
                }
            `}</style>
        </LumaAccessibleDialog>
    );
};

export default EditReglasNivelPage;
